/* Copyright (c) 2026 Hyegro Pty Ltd. All rights reserved. */

/**
 * A phone number, email or website on a v2 record page, as a link.
 *  - Email: opens the mail app (mailto:).
 *  - Website: opens in a new tab.
 *  - Phone: calls through the softphone when this user can call, filed
 *    against the record the page shows (RecordCallContext), with the same
 *    reasons as the header call button when it cannot right now. Without
 *    telephony it is a tel: link, so the computer's or phone's own dialler
 *    opens.
 * Clicks never reach the field around it, so a link does not start inline
 * editing.
 */

import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import ButtonBase from "@mui/material/ButtonBase";
import Tooltip from "@mui/material/Tooltip";
import { ExternalLink, Phone } from "lucide-react";
import { useClickToCall } from "components/Softphone/CallButton";
import { v2 as t } from "styles/tokens/v2";
import { contactLinkKind, telHref, websiteHref, websiteLabel } from "./contactLinks";
import { useRecordCallContext } from "./RecordCallContext";

const linkSx = {
  color: t.link,
  fontWeight: 500,
  textDecoration: "none",
  overflowWrap: "anywhere",
  borderRadius: "4px",
  "&:hover": { textDecoration: "underline" },
  "&:focus-visible": { outline: `2px solid ${t.link}`, outlineOffset: 2 },
};

const stop = (e) => e.stopPropagation();
const visuallyHidden = { position: "absolute", width: 1, height: 1, overflow: "hidden", clip: "rect(0 0 0 0)", whiteSpace: "nowrap" };

function PhoneLink({ value, showIcon }) {
  const context = useRecordCallContext();
  const { available, disabled, busy, tooltip, dial } = useClickToCall({ phone: value, context, onError: () => {} });
  if (!available) {
    return (
      <Box component="a" href={telHref(value)} onClick={stop} sx={linkSx}>
        {value}
      </Box>
    );
  }
  return (
    <Tooltip title={tooltip} placement="top">
      <span>
        <ButtonBase
          onClick={(e) => {
            stop(e);
            if (!disabled) dial();
          }}
          aria-disabled={disabled || undefined}
          aria-label={`Call ${value}`}
          sx={{ ...linkSx, font: "inherit", gap: 0.75, cursor: disabled ? "default" : "pointer", opacity: busy ? 0.6 : 1 }}
        >
          {showIcon && <Phone size={14} aria-hidden="true" />}
          {value}
        </ButtonBase>
      </span>
    </Tooltip>
  );
}

PhoneLink.propTypes = { value: PropTypes.string.isRequired, showIcon: PropTypes.bool.isRequired };

export default function ContactValue({ kind, value, showIcon }) {
  const v = String(value || "").trim();
  if (kind === "phone") return <PhoneLink value={v} showIcon={showIcon} />;
  if (kind === "email") {
    return (
      <Box component="a" href={`mailto:${v}`} onClick={stop} sx={linkSx}>
        {v}
      </Box>
    );
  }
  if (kind === "website") {
    return (
      <Box component="a" href={websiteHref(v)} target="_blank" rel="noopener noreferrer" onClick={stop} sx={{ ...linkSx, display: "inline-flex", alignItems: "center", gap: 0.5 }}>
        {websiteLabel(v)}
        {showIcon ? <ExternalLink size={12} aria-label="(opens in a new tab)" /> : <Box component="span" sx={visuallyHidden}>(opens in a new tab)</Box>}
      </Box>
    );
  }
  return v;
}

ContactValue.propTypes = {
  kind: PropTypes.oneOf(["phone", "email", "website"]).isRequired,
  value: PropTypes.string.isRequired,
  // Off where the value already sits beside its own icon (the Account hero).
  showIcon: PropTypes.bool,
};
ContactValue.defaultProps = { showIcon: true };

/** The value as a link when its label and content say it is one, else as given. */
export function linkedValue(label, value, fallback = value, { showIcon = true } = {}) {
  const kind = contactLinkKind(label, value);
  return kind ? <ContactValue kind={kind} value={value} showIcon={showIcon} /> : fallback;
}
